<template>
    <div id="app">
        <transition name="fade-appear">
            <div :class="'app-sidebar' + (menuVisible ? ' expanded' :'')" v-show="!hideMenu">
                <header class="app-header">
                    <h1 class="app-title">API Documentation</h1>
                    <close-menu-button @click="closeMenu"/>
                </header>
                <app-menu class="app-menu"/>
            </div>
        </transition>
        <transition name="fade-scale">
            <div class="open-menu-button-container" v-show="!menuVisible">
                <open-menu-button @click="openMenu"/>
            </div>
        </transition>
        <keep-alive :exclude="['PageView']">
            <router-view class="app-viewport" :key="$route.path"/>
        </keep-alive>
    </div>
</template>

<script>
  import CloseMenuButton from './components/CloseMenuButton';
  import IconButton      from './components/Forms/IconButton';
  import AppMenu         from './components/Layout/AppMenu';
  import Icon            from './components/Layout/Icon';
  import OpenMenuButton  from './components/OpenMenuButton';
  import { eventBus }    from './modules/eventBus';

  export default {
    components: { OpenMenuButton, CloseMenuButton, IconButton, Icon, AppMenu },

    data () {
      return {
        shortCutTimer: null,
        menuVisible:   false,
      };
    },

    computed: {
      hideMenu () {
        return this.$route.meta && this.$route.meta.hideMenu === true;
      },
    },

    mounted () {
      window.addEventListener('keyup', event => {
        if ( event.key === 'Escape' ) {
          eventBus.$emit('shortcut:close');

          return;
        }

        if ( !event.altKey ) {
          return;
        }

        switch ( event.key ) {
          case 's':
          case 'f':
            eventBus.$emit('shortcut:search');
            break;

          case 'v':
            eventBus.$emit('shortcut:version');
            break;
        }
      });
    },

    methods: {
      closeMenu () {
        this.menuVisible = false;
      },
      openMenu () {
        this.menuVisible = true;
      },
    },
  };
</script>

<!--suppress CssFloatPxLength -->
<style>
    /*************************************************************************
     * Variable declarations
     *************************************************************************/
    :root {
        --color-black:             #1a1a1a;
        --color-black-medium:      #2c2c2c;
        --color-black-light:       #444444;
        --color-gray-darker:       #555555;
        --color-gray-dark:         #777777;
        --color-gray-medium:       #999999;
        --color-gray:              #cccccc;
        --color-gray-light:        #e0e0e0;
        --color-gray-lighter:      #e7e7e7;
        --color-white:             #f2f2f2;
        --color-white-bright:      #fdfdfd;

        --color-green-dark:        #3c8e1a;
        --color-green:             #4caf50;
        --color-teal:              #009688;
        --color-orange:            #ff9300;
        --color-red:               #e44436;
        --color-pink:              #cd3292;
        --color-cyan:              #238eaa;
        --color-blue:              #3700b3;

        --color-primary:           #08c697;
        --color-primary-shade:     #007f5f;
        --color-primary-highlight: #89ead1;
        --color-primary-faded:     #cff9ec;
        --color-secondary:         #37aef3;
        /*
        --color-primary:           #0075b9;
        --color-primary-shade:     #004a89;
        --color-primary-highlight: #57a3ec;
        --color-secondary:         #78909c;
        */

        --font-family-sans-serif:  'Ubuntu', sans-serif;
        --font-family-monospace:   'Inconsolata', 'Ubuntu Mono', monospace;
        --font-family-copy:        var(--font-family-sans-serif);
        --font-family-default:     var(--font-family-sans-serif);
    }


    /*************************************************************************
     * Global styling
     *************************************************************************/
    * {
        box-sizing: border-box;
    }

    html {
        scroll-behavior: smooth;
        background:      var(--color-white-bright);
    }

    html,
    body {
        margin:     0;
        padding:    0;
        min-height: 100vh;
    }

    h1,
    h2,
    h3 {
        margin:      0;
        font-weight: 700;
    }

    h4,
    h5,
    h6 {
        font-weight:    600;
        letter-spacing: 0.25px;
    }

    h1 {
        font-weight:    800;
        letter-spacing: 0.5px;
    }

    code {
        font-size:   1rem;
        font-family: var(--font-family-monospace);
    }

    p,
    span,
    em,
    strong {
        font-family: var(--font-family-copy);
    }

    p {
        font-size:   1.1rem;
        line-height: 1.6;
    }

    hr {
        max-width:     60%;
        margin:        4rem auto;
        border:        none;
        border-bottom: 1px solid var(--color-gray-lighter);
    }

    #app {
        font-family:             var(--font-family-default);
        -webkit-font-smoothing:  antialiased;
        -moz-osx-font-smoothing: grayscale;
        color:                   #2c3e50;
    }

    .app-viewport {
    }

    .app-sidebar {
        position:       fixed;
        display:        flex;
        flex-direction: column;
        width:          100%;
        height:         100vh;
        background:     radial-gradient(var(--color-primary), var(--color-primary-shade)) fixed;
        color:          #fafafa;
        z-index:        2;
        pointer-events: none;
        opacity:        0;
        transform:      scale(1.5);
        transition:     opacity 250ms, transform 400ms;
    }

    .app-sidebar.expanded {
        pointer-events: all;
        opacity:        1;
        transform:      scale(1);
    }

    .open-menu-button-container {
        position: fixed;
        z-index:  2;
        padding:  1rem;
    }

    @media screen and (min-width: 900px) {
        .app-viewport {
            margin-left: 25rem;
        }

        .app-sidebar {
            max-width:      25rem;
            opacity:        1;
            transform:      scale(1);
            pointer-events: all;
        }

        .open-menu-button,
        .close-menu-button {
            display: none;
        }
    }

    .app-header {
        display:         flex;
        justify-content: center;
        align-items:     center;
        padding:         1rem;
        text-align:      left;
    }

    .app-title {
        margin: 0;
    }

    ::selection {
        background:  var(--color-secondary);
        color:       var(--color-white);
        text-shadow: none;
    }

    .fade-enter-active,
    .fade-leave-active {
        transition: opacity 250ms;
    }

    .fade-enter,
    .fade-leave-to {
        opacity: 0;
    }

    .fade-scale-enter-active,
    .fade-scale-leave-active {
        transition: opacity 125ms, transform 250ms;
    }

    .fade-scale-enter,
    .fade-scale-leave-to {
        opacity:   0;
        transform: scale(0.95);
    }

    .fade-appear-enter-active {
        transition: opacity 250ms, transform 350ms;
    }

    .fade-appear-leave-active {
        transition: opacity 250ms, transform 1500ms;
    }

    .fade-appear-enter,
    .fade-appear-leave-to {
        opacity:   0;
        transform: scale(1.5);
    }
</style>
